import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentsApi, CommentEntityType } from '../api/comments';
import { ApiError } from '../api/client';
import { useToast } from './Toast';

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

interface CommentThreadProps {
  projectId: number;
  entityType: CommentEntityType;
  /** Omit for the single-row-per-project sections (Work Plan, Beneficiaries, Budget, etc.) where the project itself is the context. */
  entityId?: number;
  /** Collapsed by default on dense list views (e.g. one per indicator); expanded by default on full-page sections. */
  defaultOpen?: boolean;
}

/**
 * Append-only comment history. Author, date, and time are never
 * entered by the user - they come back from the server on every
 * comment (Section 14), read from the authenticated session there,
 * never trusted from client input.
 */
export function CommentThread({ projectId, entityType, entityId, defaultOpen = false }: CommentThreadProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState('');
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['comments', projectId, entityType, entityId ?? 'project'];

  const query = useQuery({ queryKey, queryFn: () => commentsApi.list(projectId, entityType, entityId), enabled: open });

  const addMutation = useMutation({
    mutationFn: (body: string) => commentsApi.create(projectId, entityType, entityId ?? null, body),
    onSuccess: () => { setDraft(''); queryClient.invalidateQueries({ queryKey }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save comment.'),
  });

  const comments = query.data?.data ?? [];

  return (
    <div className="comment-thread">
      <button type="button" className="comment-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide comments' : `Comments${comments.length > 0 ? ` (${comments.length})` : ''}`}
      </button>

      {open && (
        <div className="comment-thread-body">
          {query.isLoading && <p className="muted">Loading comments…</p>}
          {!query.isLoading && comments.length === 0 && <p className="muted">No comments yet.</p>}

          {comments.length > 0 && (
            <ul className="comment-list">
              {comments.map((c) => (
                <li key={c.id} className="comment-item">
                  <div className="comment-meta"><strong>{c.author_name}</strong><span className="muted"> - {formatTimestamp(c.created_at)}</span></div>
                  <div className="comment-body">{c.body}</div>
                </li>
              ))}
            </ul>
          )}

          <form
            className="comment-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim().length === 0) return;
              addMutation.mutate(draft.trim());
            }}
          >
            <textarea rows={2} placeholder="Add a comment…" value={draft} onChange={(e) => setDraft(e.target.value)} />
            <button type="submit" disabled={addMutation.isPending || draft.trim().length === 0}>
              {addMutation.isPending ? 'Saving…' : 'Save Comment'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
